import { Box, Paper, Typography } from "@mui/material";
import { AuthButtons } from "Components/Auth/Shared/AuthButtons";
import { AuthPageFooter } from "Components/Auth/Shared/AuthPageFooter";
import { AuthRedirectProgress } from "Components/Auth/Shared/AuthRedirectProgress";
import { BetaInfoCard } from "Components/Auth/Shared/BetaInfoCard";
import {
  AuthDivider,
  AuthHeader,
  AuthPage,
  AuthPageContent,
} from "Components/Auth/Shared/AuthStyledComponents";
import { UnauthenticatedHeader } from "Components/Layout/UnauthenticatedHeader";
import { UnauthenticatedLayout } from "Components/Layout/UnauthenticatedLayout";
import type { RootStateType } from "State/Store";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";
import { getIsBetaVisible } from "Utils/betaUtils";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Auth.AuthPageLayout;

export type AuthPageType = "sign-up" | "sign-in" | "other";

type Props = {
  title: string;
  pageType: AuthPageType;
  children: ReactNode;
  isLoading?: boolean;
  userType?: AccessRoleCode;
  isSSODisabled?: boolean;
  /** Result pages (a confirmed e-mail link) show an icon above a centred title. */
  icon?: ReactNode;
  /** The sign-in / sign-up prompt below the card; pointless when the person is already signed in. */
  isFooterHidden?: boolean;
};

const StyledPaper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(7, 2)};
  border-radius: 10px;
`;

export const AuthPageLayout = ({
  children,
  isLoading = false,
  pageType,
  title,
  userType,
  isSSODisabled,
  icon,
  isFooterHidden = false,
}: Props) => {
  const ssoIsLoading = useSelector((e: RootStateType) => e.auth.ssoIsLoading);
  const { t } = useResource();

  const isHunterFlow =
    userType === AccessRoleCodes.Hunter ||
    userType === AccessRoleCodes.HunterPlus;

  return (
    <UnauthenticatedLayout title={title}>
      <AuthPage>
        <AuthPageContent>
          <div>
            <UnauthenticatedHeader userType={userType} />
          </div>
          {!isHunterFlow && getIsBetaVisible() && (
            <Box mb={2}>
              <BetaInfoCard />
            </Box>
          )}
          <StyledPaper>
            {icon && (
              <Box display="flex" justifyContent="center" mb={3}>
                {icon}
              </Box>
            )}
            <AuthHeader
              variant="h2"
              fontSize={["1.875rem", "2.25rem"]}
              textAlign={icon ? "center" : undefined}
            >
              {title}
            </AuthHeader>
            {ssoIsLoading ? (
              <AuthRedirectProgress />
            ) : (
              <>
                {children}
                {pageType !== "other" && !isSSODisabled && (
                  <Box px={4}>
                    <AuthDivider>
                      <Typography paddingX="10px">
                        {t(COMPONENT_RESOURCES.DividerText)}
                      </Typography>
                    </AuthDivider>
                    <AuthButtons isLoading={isLoading} />
                  </Box>
                )}
              </>
            )}
          </StyledPaper>
          {!isFooterHidden && <AuthPageFooter pageType={pageType} />}
        </AuthPageContent>
      </AuthPage>
    </UnauthenticatedLayout>
  );
};
